import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

function ReportesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = isDark ? 'dark' : 'light';
  const [activeTab, setActiveTab] = useState('ventas-vendedor');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Estados para datos
  const [salesBySeller, setSalesBySeller] = useState([]);
  const [salesByPos, setSalesByPos] = useState([]);
  const [salesByPlan, setSalesByPlan] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);

  useEffect(() => {
    fetchData();
  }, [activeTab, dateRange, startDate, endDate]);

  const getDateParams = () => {
    let start = null;
    let end = null;

    if (dateRange === 'today') {
      // Obtener fecha local en formato YYYY-MM-DD
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      start = `${year}-${month}-${day}`;
      end = start;
    } else if (dateRange === 'custom' && startDate && endDate) {
      start = startDate;
      end = endDate;
    }

    return { start, end };
  };

  const fetchData = async () => {
    setLoading(true);
    const { start, end } = getDateParams();
    const token = localStorage.getItem('token');

    const params = new URLSearchParams();
    if (start) params.append('start_date', start);
    if (end) params.append('end_date', end);

    try {
      let endpoint = '';
      switch (activeTab) {
        case 'ventas-vendedor':
          endpoint = '/api/reports/sales-by-seller';
          break;
        case 'ventas-pos':
          endpoint = '/api/reports/sales-by-pos';
          break;
        case 'ventas-plan':
          endpoint = '/api/reports/sales-by-plan';
          break;
        case 'retiros':
          endpoint = '/api/reports/withdrawals';
          break;
        case 'abonos':
          endpoint = '/api/reports/deposits';
          break;
        default:
          endpoint = '/api/reports/sales-by-seller';
      }

      const response = await fetch(`${API_URL}${endpoint}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        switch (activeTab) {
          case 'ventas-vendedor':
            setSalesBySeller(data);
            break;
          case 'ventas-pos':
            setSalesByPos(data);
            break;
          case 'ventas-plan':
            setSalesByPlan(data);
            break;
          case 'retiros':
            setWithdrawals(data);
            break;
          case 'abonos':
            setDeposits(data);
            break;
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderSalesChart = (data, dataKey, nameKey, title) => {
    if (!data || data.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No hay datos para mostrar en el rango seleccionado
        </div>
      );
    }

    const totalAmount = data.reduce((sum, item) => sum + item.total_amount, 0);
    const totalVouchers = data.reduce((sum, item) => sum + item.total_vouchers, 0);

    return (
      <div className="space-y-6">
        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Ventas</p>
            <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalAmount.toFixed(2)}
            </p>
          </div>
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-green-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Fichas</p>
            <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              {totalVouchers}
            </p>
          </div>
        </div>

        {/* Gráfica de Barras */}
        <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Ventas por {title}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#374151' : '#E5E7EB'} />
              <XAxis 
                dataKey={nameKey} 
                stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'}
                tick={{ fill: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}
              />
              <YAxis 
                stroke={theme === 'dark' ? '#9CA3AF' : '#6B7280'}
                tick={{ fill: theme === 'dark' ? '#9CA3AF' : '#6B7280' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme === 'dark' ? '#374151' : '#FFFFFF',
                  border: `1px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  color: theme === 'dark' ? '#F3F4F6' : '#1F2937'
                }}
              />
              <Legend />
              <Bar dataKey="total_amount" fill="#3B82F6" name="Monto Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfica de Pastel */}
        <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Distribución de Fichas
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey="total_vouchers"
                nameKey={nameKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry[nameKey]}: ${entry.total_vouchers}`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme === 'dark' ? '#374151' : '#FFFFFF',
                  border: `1px solid ${theme === 'dark' ? '#4B5563' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  color: theme === 'dark' ? '#F3F4F6' : '#1F2937'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla detallada */}
        <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Detalle
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {title}
                  </th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fichas
                  </th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Monto Total
                  </th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Promedio
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr 
                    key={index}
                    className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {item[nameKey]}
                    </td>
                    <td className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {item.total_vouchers}
                    </td>
                    <td className={`text-right py-3 px-4 font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                      ${item.total_amount.toFixed(2)}
                    </td>
                    <td className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      ${(item.total_amount / item.total_vouchers).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderWithdrawals = () => {
    if (!withdrawals || withdrawals.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No hay retiros registrados en el rango seleccionado
        </div>
      );
    }

    const totalWithdrawn = withdrawals.reduce((sum, w) => sum + w.amount_withdrawn, 0);
    const totalCommission = withdrawals.reduce((sum, w) => sum + w.seller_commission, 0);
    const totalAdminReceives = withdrawals.reduce((sum, w) => sum + w.admin_receives, 0);

    return (
      <div className="space-y-6">
        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-red-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Retirado</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalWithdrawn.toFixed(2)}
            </p>
          </div>
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-yellow-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Comisiones Vendedores</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalCommission.toFixed(2)}
            </p>
          </div>
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-green-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Admin Recibió</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalAdminReceives.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Tabla */}
        <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Historial de Retiros
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Fecha</th>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Vendedor</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Retirado</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Comisión</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Admin Recibe</th>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Notas</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal, index) => (
                  <tr 
                    key={index}
                    className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {new Date(withdrawal.withdrawal_date).toLocaleDateString()}
                    </td>
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {withdrawal.seller_name}
                    </td>
                    <td className={`text-right py-3 px-4 font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                      ${withdrawal.amount_withdrawn.toFixed(2)}
                    </td>
                    <td className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      ${withdrawal.seller_commission.toFixed(2)}
                    </td>
                    <td className={`text-right py-3 px-4 font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                      ${withdrawal.admin_receives.toFixed(2)}
                    </td>
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                      {withdrawal.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDeposits = () => {
    if (!deposits || deposits.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No hay abonos registrados en el rango seleccionado
        </div>
      );
    }

    const totalDeposited = deposits.reduce((sum, d) => sum + d.amount_deposited, 0);
    const totalCommission = deposits.reduce((sum, d) => sum + d.commission_added, 0);
    const totalAdded = deposits.reduce((sum, d) => sum + d.total_added, 0);

    return (
      <div className="space-y-6">
        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Abonado (Base)</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalDeposited.toFixed(2)}
            </p>
          </div>
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Comisiones Agregadas</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalCommission.toFixed(2)}
            </p>
          </div>
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-green-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Total Agregado</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
              ${totalAdded.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Tabla */}
        <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Historial de Abonos
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Fecha</th>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Vendedor</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Monto Base</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Comisión</th>
                  <th className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Total Agregado</th>
                  <th className={`text-left py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Notas</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit, index) => (
                  <tr 
                    key={index}
                    className={`border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {new Date(deposit.deposit_date).toLocaleDateString()}
                    </td>
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      {deposit.seller_name}
                    </td>
                    <td className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                      ${deposit.amount_deposited.toFixed(2)}
                    </td>
                    <td className={`text-right py-3 px-4 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`}>
                      ${deposit.commission_added.toFixed(2)}
                    </td>
                    <td className={`text-right py-3 px-4 font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                      ${deposit.total_added.toFixed(2)}
                    </td>
                    <td className={`py-3 px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                      {deposit.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className={`shadow-sm transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
              }`}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Reportes
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Análisis de ventas y estadísticas
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Filtro de Fecha */}
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6 mb-6`}>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                Rango de Fecha
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-800'
                } focus:ring-2 focus:ring-blue-500`}
              >
                <option value="today">Hoy</option>
                <option value="custom">Rango Personalizado</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <>
                <div className="flex-1 min-w-[200px]">
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-800'
                    } focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-800'
                    } focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'ventas-vendedor', label: 'Ventas por Vendedor' },
            { id: 'ventas-pos', label: 'Ventas por Punto de Venta' },
            { id: 'ventas-plan', label: 'Ventas por Plan' },
            { id: 'retiros', label: 'Retiros' },
            { id: 'abonos', label: 'Abonos' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          {loading ? (
            <div className="text-center py-8">
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Cargando datos...</p>
            </div>
          ) : (
            <>
              {activeTab === 'ventas-vendedor' && renderSalesChart(salesBySeller, 'total_amount', 'seller_name', 'Vendedor')}
              {activeTab === 'ventas-pos' && renderSalesChart(salesByPos, 'total_amount', 'pos_name', 'Punto de Venta')}
              {activeTab === 'ventas-plan' && renderSalesChart(salesByPlan, 'total_amount', 'plan_name', 'Plan')}
              {activeTab === 'retiros' && renderWithdrawals()}
              {activeTab === 'abonos' && renderDeposits()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportesPage;
